// pages/About.js
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LightHeader from '../components/LightHeader';
import LightFooter from '../components/LightFooter';
import TransitionLink from '../components/TransitionLink';
import AboutTimeline from '../components/AboutTimeline';
import Skills from '../components/Skills';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';
import SEO from '../components/SEO';

const About = () => {
    const { language } = useLanguage();
    const t = useTranslation(language);
    const sectionRef = useRef(null);
    const itemsRef = useRef([]);
    const location = useLocation();
    const [isHovering, setIsHovering] = useState(false);
    const [isHoveringBack, setIsHoveringBack] = useState(false);

    // Enhanced scroll to top on navigation
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

        const timer = setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }, 100);

        return () => clearTimeout(timer);
    }, [location.pathname]);

    // Intersection Observer for fade-in animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) observer.observe(sectionRef.current);
        itemsRef.current.forEach((item) => {
            if (item) observer.observe(item);
        });

        return () => observer.disconnect();
    }, []);

    // Dark theme class on body
    useEffect(() => {
        document.body.classList.add('about-dark-theme');
        return () => document.body.classList.remove('about-dark-theme');
    }, []);

    const seoDescriptions = {
        en: "Fifteen years building broadcast graphics for Italian national television, including Fiorello's Viva Rai2, Alberto Angela's Ulisse, and Dolce&Gabbana. My story, skills and career timeline.",
        it: "Quindici anni a costruire grafica broadcast per la televisione nazionale italiana, tra cui Viva Rai2, Ulisse e Dolce&Gabbana. La mia storia, le mie competenze e il percorso professionale."
    };

    const aboutSchema = {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": language === 'it' ? "Chi sono - Paolo Allegretti" : "About - Paolo Allegretti",
        "description": seoDescriptions[language],
        "url": "https://paoloallegretti.com/about",
        "mainEntity": {
            "@type": "Person",
            "name": "Paolo Allegretti",
            "jobTitle": "Senior Creative Technologist",
            "description": seoDescriptions[language],
            "hasOccupation": [
                { "@type": "Occupation", "name": "Broadcast Graphics Designer" },
                { "@type": "Occupation", "name": "Full-Stack Developer" },
                { "@type": "Occupation", "name": "Virtual Production Specialist" }
            ]
        }
    };

    return (
        <>
            <SEO
                title={language === 'it' ? "Chi sono" : "About"}
                description={seoDescriptions[language]}
                url="/about"
                language={language}
                schema={aboutSchema}
            />

            <div className="min-h-screen bg-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <LightHeader ctaLabel={t('getInTouch')} />

                <section
                    ref={sectionRef}
                    className="w-full pt-40 pb-24 px-8 md:px-16 transition-opacity duration-700 opacity-0"
                >
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 mb-8">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
                            </span>
                            <span className="text-white/60 text-xs font-mono tracking-widest uppercase">
                                {t('availableForWork')}
                            </span>
                        </div>

                        <p
                            ref={(el) => (itemsRef.current[1] = el)}
                            className="text-2xl md:text-4xl text-white leading-tight opacity-0 transform translate-y-10 transition-all duration-700 delay-100 font-light tracking-tight whitespace-pre-line"
                        >
                            {t('aboutIntroPage')}
                        </p>
                    </div>
                </section>

                <section className="w-full bg-black px-8 md:px-16">
                    <div className="max-w-5xl mx-auto">
                        <AboutTimeline />
                    </div>
                </section>

                <Skills />

                <section className="w-full bg-black py-24 px-8 md:px-16">
                    <div className="max-w-4xl">
                        <p
                            ref={(el) => (itemsRef.current[4] = el)}
                            className="text-2xl md:text-3xl text-white leading-tight mb-10 font-light tracking-tight opacity-0 transform translate-y-10 transition-all duration-700 delay-100 whitespace-pre-line"
                        >
                            {t('contactIntroPage')}
                        </p>

                        <a
                            href="mailto:info@paoloallegretti.com"
                            className="inline-flex items-center text-white font-medium text-2xl md:text-4xl group"
                        >
                            <span className="border-b border-white/30 group-hover:border-orange-400 group-hover:text-orange-400 transition-colors duration-300 pb-0.5">
                                info@paoloallegretti.com
                            </span>
                            <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300">→</span>
                        </a>

                        {/* Bottom navigation */}
                        <div
                            ref={(el) => (itemsRef.current[3] = el)}
                            className="flex justify-between items-center mt-20 opacity-0 transform translate-y-10 transition-all duration-700 delay-200"
                        >
                            <TransitionLink
                                to="/"
                                className="header-nav-link relative group flex items-center overflow-hidden"
                                onMouseEnter={() => setIsHoveringBack(true)}
                                onMouseLeave={() => setIsHoveringBack(false)}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`mr-2 w-5 h-5 text-white group-hover:text-orange-400 transition-all duration-300 ease-out transform ${isHoveringBack ? '-translate-x-1' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                <span className="text-base md:text-lg text-white group-hover:text-orange-400 transition-colors duration-300">
                                    {t('backHome')}
                                </span>
                            </TransitionLink>

                            <TransitionLink
                                to="/code"
                                className="header-nav-link relative group flex items-center overflow-hidden"
                                onMouseEnter={() => setIsHovering(true)}
                                onMouseLeave={() => setIsHovering(false)}
                            >
                                <span className="text-base md:text-lg text-white group-hover:text-orange-400 transition-colors duration-300">
                                    {t('goToDeveloper')}
                                </span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`ml-2 w-5 h-5 text-white group-hover:text-orange-400 transition-all duration-300 ease-out transform ${isHovering ? 'translate-x-1' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </TransitionLink>
                        </div>
                    </div>
                </section>

                <div className="px-8 md:px-16 pb-8">
                    <LightFooter fullPortfolioLabel={language === 'it' ? 'Portfolio completo' : 'Full portfolio'} />
                </div>
            </div>
        </>
    );
};

export default About;
